import { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';

import '@uppy/core/css/style.min.css';
import '@uppy/status-bar/css/style.min.css';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useUppyUploadContext } from '../../contexts/UppyUploadContext/hooks/useUppyUploadContext.hook';
import { humanizeTusCategory } from '../../contexts/UppyUploadContext/utils/humanize-tus-category.util';

import { UploadsTrayInstanceStatusBar } from './UploadsTrayInstanceStatusBar';

// Always mounted in the AppBar, so upload progress stays visible even after the page that
// started it (e.g. a mission's Files panel) has been navigated away from. One status bar
// is installed per live Uppy instance from the shared registry (see UppyUploadContext),
// each labeled by its scope so several missions uploading at once stay distinguishable.
export const UploadsTray = () => {
  const { instances, activeFilesCount, hasUploadError } =
    useUppyUploadContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (activeFilesCount === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label="Envois de fichiers en cours"
        color="inherit"
        onClick={handleOpen}
      >
        <Badge
          badgeContent={activeFilesCount}
          color={hasUploadError ? 'error' : 'secondary'}
        >
          <CloudUploadIcon />
        </Badge>
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={handleClose}
        open={!!anchorEl}
      >
        <Box sx={{ maxHeight: 480, minWidth: 360, overflow: 'auto', p: 2 }}>
          {instances.map(({ id, scope, uppy }, index) => (
            <Box key={id}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Typography color="text.secondary" variant="caption">
                {scope.label ?? humanizeTusCategory(scope.tusCategory)}
              </Typography>
              {!!scope.fileCategoryLabel && (
                <Typography
                  color="text.disabled"
                  sx={{ display: 'block' }}
                  variant="caption"
                >
                  {scope.fileCategoryLabel}
                </Typography>
              )}
              <UploadsTrayInstanceStatusBar uppy={uppy} />
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
